import { useState } from "react";
import { useIsAdmin } from "@/lib/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Plus, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import type { ApiToken } from "@/lib/types";
import { BRANDS } from "@/lib/brands";
import { formatAgo, formatDate, formatDateTime } from "@/lib/format";
import { BrandMark } from "@/components/BrandMark";
import { Empty } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { LoadError } from "@/components/LoadError";

const KINDS: { value: ApiToken["kind"]; label: string; help: string }[] = [
  { value: "mcp", label: "AI assistant (MCP)", help: "Claude Desktop, Claude Code, Grok: connects through the MCP server." },
  { value: "site", label: "Website", help: "One university site: sends forms and checks logins for its brand." },
  { value: "api", label: "Script or report", help: "A script, report or tool that calls the REST API." },
  { value: "integration", label: "Integration", help: "Zapier or another outside service." },
];

const PRESETS: { key: string; label: string; scopes: string[]; help: string }[] = [
  { key: "full", label: "Full access", scopes: ["*"], help: "Everything, including money operations (each charge or refund still needs a confirm step)." },
  { key: "read", label: "Read only", scopes: ["*:read"], help: "Can look at everything, change nothing." },
  { key: "site", label: "Website forms and logins", scopes: ["forms:write", "logins:write"], help: "What a university site needs. Pick its brand below." },
  { key: "contacts", label: "Contacts and tags", scopes: ["contacts:write"], help: "Read and change contacts, notes and tags only." },
  { key: "custom", label: "Custom…", scopes: [], help: "Type scopes separated by spaces, e.g. contacts:read email:read." },
];

function scopeText(scopes: string[]): string {
  if (scopes.includes("*")) return "Full access";
  if (scopes.length === 1 && scopes[0] === "*:read") return "Read only";
  return scopes.join(" ");
}

/** API and MCP tokens: list, create (the secret is shown once), revoke. */
export function TokensPanel() {
  const isAdmin = useIsAdmin();
  const qc = useQueryClient();
  const toast = useToast();
  const [showRevoked, setShowRevoked] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiToken | null>(null);
  const tokens = useQuery({
    queryKey: ["tokens", showRevoked],
    queryFn: () => api<{ tokens: ApiToken[] }>(`/api/v1/tokens${showRevoked ? "?include_revoked=true" : ""}`),
  });
  const revoke = useMutation({
    mutationFn: (id: number) => api(`/api/v1/tokens/${id}/revoke`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tokens"] });
      toast("Token revoked. Every server stops accepting it within a minute.");
      setRevoking(null);
    },
    onError: (e) => toast(e.message, "error"),
  });
  const rows = tokens.data?.tokens ?? [];
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>API and MCP tokens</CardTitle>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)]">
            <Checkbox checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} /> Show revoked
          </label>
          {isAdmin && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus /> New token
          </Button>
          )}
        </div>
      </CardHeader>
      {!isAdmin && <p className="px-4 pt-2 text-xs text-[var(--color-warning-fg)]">Only an admin can change these. You can look.</p>}
      <p className="px-4 py-2 text-sm text-[var(--color-muted-foreground)]">
        Each assistant, site or script gets its own token, so one can be revoked without touching the others. The secret is shown once when it's
        made; the CRM keeps only a fingerprint of it.
      </p>
      {tokens.isLoading && <Skeleton className="m-3 h-32" />}
      {tokens.error && <LoadError what="tokens" error={tokens.error} onRetry={() => tokens.refetch()} />}
      {!tokens.isLoading && !tokens.error && rows.length === 0 && <Empty title="No tokens yet." />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Name</TH>
              <TH>For</TH>
              <TH>Can do</TH>
              <TH>Brand</TH>
              <TH>Starts with</TH>
              <TH>Last used</TH>
              <TH>Made</TH>
              <TH>Expires</TH>
              <TH />
            </tr>
          </THead>
          <tbody>
            {rows.map((t) => (
              <TR key={t.id} className={t.revoked_at ? "row-dim" : ""}>
                <TD className="font-medium">{t.name}</TD>
                <TD>{KINDS.find((k) => k.value === t.kind)?.label ?? t.kind}</TD>
                <TD className="max-w-[14rem] truncate" title={t.scopes.join(" ")}>
                  {scopeText(t.scopes)}
                </TD>
                <TD>{t.brand_id ? <BrandMark id={t.brand_id} /> : "—"}</TD>
                <TD className="num text-xs">{t.token_prefix}…</TD>
                <TD className="whitespace-nowrap" title={formatDateTime(t.last_used_at)}>
                  {t.last_used_at ? formatAgo(t.last_used_at) : "never"}
                </TD>
                <TD className="whitespace-nowrap" title={`by ${t.created_by}`}>
                  {formatDate(t.created_at)}
                </TD>
                <TD className="whitespace-nowrap">{t.expires_at ? formatDate(t.expires_at) : "never"}</TD>
                <TD className="text-right">
                  {t.revoked_at ? (
                    <Badge variant="muted">revoked {formatDate(t.revoked_at)}</Badge>
                  ) : !isAdmin ? null : (
                    <Button size="sm" variant="ghost" className="text-[var(--color-destructive)]" onClick={() => setRevoking(t)}>
                      Revoke
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
      {creating && <CreateTokenDialog onClose={() => setCreating(false)} />}
      <Dialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)}>
        <DialogContent>
          <DialogTitle>Revoke “{revoking?.name}”?</DialogTitle>
          <DialogDescription>Whatever uses this token stops working within a minute and needs a new one. This can't be undone.</DialogDescription>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRevoking(null)}>
              Keep it
            </Button>
            <Button variant="destructive" disabled={revoke.isPending} onClick={() => revoking && revoke.mutate(revoking.id)}>
              Revoke token
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function CreateTokenDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ApiToken["kind"]>("mcp");
  const [preset, setPreset] = useState("full");
  const [custom, setCustom] = useState("");
  const [brand, setBrand] = useState<number | "">("");
  const [days, setDays] = useState("");
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const scopes = preset === "custom" ? custom.split(/[\s,]+/).filter(Boolean) : (PRESETS.find((p) => p.key === preset)?.scopes ?? []);
  const create = useMutation({
    mutationFn: () =>
      api<{ token: string; info: ApiToken }>("/api/v1/tokens", {
        json: { name: name.trim(), kind, scopes, brand_id: brand || undefined, expires_in_days: days ? Number(days) : undefined },
      }),
    onSuccess: (r) => {
      setSecret(r.token);
      void qc.invalidateQueries({ queryKey: ["tokens"] });
    },
  });
  const close = () => {
    setSecret(null);
    onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !secret && close()}>
      <DialogContent className="w-[min(620px,calc(100vw-2rem))]" onInteractOutside={(e) => secret && e.preventDefault()}>
        {secret ? (
          <>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="size-4" /> Copy the token now
            </DialogTitle>
            <DialogDescription>
              This is the only time it's shown. Save it in 1Password (or paste it where it's needed) before closing this window. If it's lost, revoke it and make a
              new one.
            </DialogDescription>
            <div className="flex items-center gap-2 rounded-md border bg-[var(--color-muted)] p-2">
              <code className="min-w-0 flex-1 break-all text-sm" data-secret>
                {secret}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void navigator.clipboard?.writeText(secret).then(() => setCopied(true))}
                aria-label="Copy the token"
              >
                <Copy /> {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={close}>I've saved it</Button>
            </div>
          </>
        ) : (
          <>
            <DialogTitle>New token</DialogTitle>
            <DialogDescription>Name it after who or what uses it, so the audit log says who did what.</DialogDescription>
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                create.mutate();
              }}
            >
              <div className="space-y-1">
                <Label htmlFor="tok-name">Name</Label>
                <Input id="tok-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="claude-desktop, grok-broadcast, mhu-site" required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="tok-kind">Used by</Label>
                <Select id="tok-kind" value={kind} onChange={(e) => {
                  const k = e.target.value as ApiToken["kind"];
                  setKind(k);
                  if (k === "site") setPreset("site");
                }}>
                  {KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-[var(--color-muted-foreground)]">{KINDS.find((k) => k.value === kind)?.help}</p>
              </div>
              <fieldset className="space-y-1">
                <legend className="text-xs font-medium text-[var(--color-muted-foreground)]">What it can do</legend>
                {PRESETS.map((p) => (
                  <label key={p.key} className="flex items-start gap-2 text-sm">
                    <input type="radio" name="preset" className="mt-1 accent-[var(--color-primary)]" checked={preset === p.key} onChange={() => setPreset(p.key)} />
                    <span>
                      {p.label}
                      <span className="block text-xs text-[var(--color-muted-foreground)]">{p.help}</span>
                    </span>
                  </label>
                ))}
                {preset === "custom" && <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="contacts:read email:read" aria-label="Scopes" />}
              </fieldset>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="tok-brand">Brand {kind === "site" ? "(required for a site)" : "(optional)"}</Label>
                  <Select id="tok-brand" value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")} required={kind === "site"}>
                    <option value="">None</option>
                    {BRANDS.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.short}: {b.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="tok-days">Expires after (days, optional)</Label>
                  <Input id="tok-days" type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} placeholder="never" />
                </div>
              </div>
              {scopes.includes("*") && (
                <p className="flex items-start gap-2 rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-fg)]">
                  <ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> Full access can change anything and start charges and refunds (each still asks for a confirm). Give it only to
                  assistants you run yourself.
                </p>
              )}
              {create.error && <p className="text-sm text-[var(--color-destructive)]">{create.error.message}</p>}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={close}>
                  Cancel
                </Button>
                <Button type="submit" disabled={create.isPending || !name.trim() || scopes.length === 0}>
                  Make token
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
